import { FormEvent, useEffect, useMemo } from 'react'
import { useTranslation } from 'next-i18next'

import Language from '~/components/PanelSettingsForm/Language/Language'
import LocationSetting from '~/components/PanelSettingsForm/LocationSetting'
import PersonalInfo from '~/components/PanelSettingsForm/PersonalInfo'
import PreferredDays from '~/components/PanelSettingsForm/PreferredDays'
import SchoolCountry from '~/components/PanelSettingsForm/SchoolCountry'
import SubjectsSetting from '~/components/PanelSettingsForm/SubjectSetting'
import Traits from '~/components/PanelSettingsForm/Traits'
import useSettingsFormContext from '~/hooks/useSettingsFormContext'
import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import { UserRole } from '~/types/user-info-types'
import { UserSettingsErrors } from '~/contexts/SettingsFormContext'

export default function StudentSettingsForm(): React.ReactElement {
  const { t } = useTranslation('generics')
  const { fields, setErrors, onSubmit } = useSettingsFormContext()
  const role = 'student'

  const formErrors = useMemo(() => {
    const requiredFields = {
      city: fields.city,
      countryCallingCode: fields.countryCallingCode,
      phone: fields.phone,
      fullName: fields.fullName,
      languagePrimary: fields.languagePrimary,
      levelsOfEducation: fields.subjects.levelsOfEducation,
      schoolCountry: fields.schoolCountry,
      subjects: fields.subjects.subjects,
      timeslots: fields.timeslots,
      traits: fields.traits,
    }

    return Object.entries(requiredFields).reduce((currErrors, [key, value]) => {
      if (!value || (typeof value === 'string' && value.trim().length === 0)) {
        currErrors[key] = 'Field required'
      }

      if (Array.isArray(value) && value.length === 0) {
        currErrors[key] = 'Choose at least one value'
      }

      return currErrors
    }, {} as UserSettingsErrors)
  }, [fields])

  useEffect(() => {
    setErrors(formErrors)
  }, [formErrors, setErrors])

  const hasError = Object.keys(formErrors).length > 0

  const handleOnSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    if (hasError) return
    onSubmit(fields)
  }

  return (
    <form className="lg:max-w-md space-y-4" onSubmit={handleOnSubmit}>
      <PersonalInfo role={role} />
      <SchoolCountry />
      <SubjectsSetting role={role} />
      <PreferredDays role={UserRole.USER} />
      <LocationSetting role={role} />
      <Language />
      <Traits role={UserRole.USER} />

      <div className="flex flex-col lg:flex-row-reverse space-y-4 lg:space-y-0 lg:space-x-4 lg:space-x-reverse">
        <ButtonPrimary disabled={false && hasError} isFullWidth size="small" type="submit">
          {t('save')}
        </ButtonPrimary>
      </div>
    </form>
  )
}
